/**
 * A shake effect for static PIXI.DisplayObjects.
 *
 * Created: 2018-04-25
 * @author jonas
 */
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {PointLike} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {Linear, TimelineLite, TimelineMax, TweenLite} from "gsap"

export class ShakeConfig {
    get stepMaxDistance():number {
        return this._stepMaxDistance;
    }

    get stepDuration():number {
        return this._stepDuration;
    }

    private readonly _stepMaxDistance:number;
    private readonly _stepDuration:number;

    /**
     * Configuration for ShakeEffect
     * @param {number} stepDuration The duration of each step (movement) in the shake.
     * @param {number} stepMaxDistance The max distance an object moves in one step. E.g stepMaxDistance = 5 will
     * result in a individual random movement in the range from -5 to 5 in both x and y axis.
     */
    constructor(stepMaxDistance:number = 4, stepDuration:number = 0.03) {
        this._stepMaxDistance = stepMaxDistance;
        this._stepDuration = stepDuration;
    }
}


export class ShakeEffect {
    private _animation:TimelineLite | null;
    private readonly _objects:PIXI.DisplayObject[];
    private _config:ShakeConfig;
    private _ending:TimelineLite;

    /**
     * Creates a reusable shake effect.
     *
     * @param {PIXI.DisplayObject[]} shakeObjects The display objects that will be shaken. The shake effect will
     * only recalculate object positions on Resize so it wont work wery well with moving objects.
     * @param {ShakeConfig} shakeConfig Shake settings.
     */
    constructor(shakeObjects:PIXI.DisplayObject[], shakeConfig:ShakeConfig) {
        this._objects = shakeObjects;
        this._config = shakeConfig;
    }

    /**
     *
     * @param {number} duration The length of the shaking in seconds.
     * @returns {TimelineLite} A timeline with the shake animation.
     */
    public getOneShotShake(duration:number):TimelineLite {
        const tl = new TimelineLite();
        tl.add(() => this.startShake());
        tl.add(() => this.stopShake(), duration);
        return tl;
    }

    /**
     * Starts the shaking animation (loop)
     */
    public startShake() {
        if(!this._animation) {
            this._animation = this.createShakeAnimation();
            this._ending = this.createShakeAnimation(true);
        }
        this._animation.play(0);
    }

    /**
     * Stops the shaking animation
     */
    public stopShake() {
        if(!this._animation) {
            return;
        }
        this._animation.pause(0);
        this._animation.kill();
        this._animation = null;
        this._ending.play(0);
    }

    private createShakeAnimation(ending:boolean = false):TimelineLite {
        const shakeAnimation = new TimelineLite();
        const repeat:number = ending ? 0 : -1;

        for(let displayObject of this._objects) {
            const shake = new TimelineMax({repeat : repeat});

            for(let i:number = 0; i < 10; i++) {
                const randomDistance:PointLike = ending ? this.getRandomPoint(i) : this.getRandomPoint();
                shake.add(new TweenLite(displayObject.position, this._config.stepDuration, {
                    x : displayObject.x + randomDistance.x,
                    y : displayObject.y + randomDistance.y,
                    ease : Linear.easeNone
                }));
                shake.add(new TweenLite(displayObject.position, 0.01, {
                    x : displayObject.x,
                    y : displayObject.y,
                    ease : Linear.easeNone
                }));
            }

            shakeAnimation.add(shake, 0);
        }
        shakeAnimation.pause();
        return shakeAnimation;
    }

    private getRandomPoint(index?:number):PointLike {
        let max = this._config.stepMaxDistance;
        if(index) {
            max = this._config.stepMaxDistance / index;
        }
        return {
            x : MathHelper.randomNumberInRange(-max, max),
            y : MathHelper.randomNumberInRange(-max, max)
        }
    }
}